// Timezone utility functions and mappings

// Common timezones with their labels
export const COMMON_TIMEZONES = [
  { code: 'Asia/Kolkata', name: 'India Standard Time (IST)', offset: '+05:30' },
  { code: 'America/New_York', name: 'Eastern Time (ET)', offset: '-05:00' },
  { code: 'America/Los_Angeles', name: 'Pacific Time (PT)', offset: '-08:00' },
  { code: 'Europe/London', name: 'Greenwich Mean Time (GMT)', offset: '+00:00' },
  { code: 'Europe/Paris', name: 'Central European Time (CET)', offset: '+01:00' },
  { code: 'Asia/Tokyo', name: 'Japan Standard Time (JST)', offset: '+09:00' },
  { code: 'Asia/Dubai', name: 'Gulf Standard Time (GST)', offset: '+04:00' },
  { code: 'Asia/Singapore', name: 'Singapore Standard Time (SGT)', offset: '+08:00' },
  { code: 'Australia/Sydney', name: 'Australian Eastern Time (AET)', offset: '+10:00' },
  { code: 'America/Toronto', name: 'Eastern Time (Canada)', offset: '-05:00' },
  { code: 'Asia/Shanghai', name: 'China Standard Time (CST)', offset: '+08:00' },
  { code: 'Europe/Berlin', name: 'Central European Time (Berlin)', offset: '+01:00' },
  { code: 'Asia/Hong_Kong', name: 'Hong Kong Time (HKT)', offset: '+08:00' },
  { code: 'America/Chicago', name: 'Central Time (CT)', offset: '-06:00' },
  { code: 'Europe/Moscow', name: 'Moscow Standard Time (MSK)', offset: '+03:00' },
];

// Get timezone name from timezone code
export const getTimezoneName = (timezoneCode = 'Asia/Kolkata') => {
  const timezone = COMMON_TIMEZONES.find(tz => tz.code === timezoneCode);
  return timezone ? timezone.name : timezoneCode;
};

// Format date with timezone
export const formatDateWithTimezone = (date, timezone = 'Asia/Kolkata', options = {}) => {
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: timezone,
  };
  
  const formatOptions = { ...defaultOptions, ...options };
  
  try {
    return new Date(date).toLocaleDateString('en-IN', formatOptions);
  } catch (error) {
    console.error('Error formatting date with timezone:', error);
    return new Date(date).toLocaleDateString();
  }
};

// Format time with timezone
export const formatTimeWithTimezone = (time, timezone = 'Asia/Kolkata', options = {}) => {
  const defaultOptions = {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  };
  
  const formatOptions = { ...defaultOptions, ...options };
  
  try {
    // If time is just a time string (HH:MM), create a date object
    let dateObj;
    if (typeof time === 'string' && time.match(/^\d{2}:\d{2}$/)) {
      dateObj = new Date(`2000-01-01T${time}:00`);
    } else {
      dateObj = new Date(time);
    }
    
    return dateObj.toLocaleTimeString('en-IN', formatOptions);
  } catch (error) {
    console.error('Error formatting time with timezone:', error);
    return time;
  }
};

// Format datetime with timezone
export const formatDateTimeWithTimezone = (datetime, timezone = 'Asia/Kolkata', options = {}) => {
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  };
  
  const formatOptions = { ...defaultOptions, ...options };
  
  try {
    return new Date(datetime).toLocaleString('en-IN', formatOptions);
  } catch (error) {
    console.error('Error formatting datetime with timezone:', error);
    return new Date(datetime).toLocaleString();
  }
};

// Convert appointment date and time to timezone-aware datetime string
export const formatAppointmentDateTime = (date, time, timezone = 'Asia/Kolkata') => {
  try {
    // Combine date and time into a datetime string
    const datetimeString = `${date}T${time}:00`;
    const datetime = new Date(datetimeString);
    
    return formatDateTimeWithTimezone(datetime, timezone, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    console.error('Error formatting appointment datetime:', error);
    return `${formatDateWithTimezone(date, timezone)} ${formatTimeWithTimezone(time, timezone)}`;
  }
};

// Get current date in specific timezone
export const getCurrentDateInTimezone = (timezone = 'Asia/Kolkata') => {
  try {
    return new Date().toLocaleDateString('en-CA', { timeZone: timezone }); // en-CA gives YYYY-MM-DD format
  } catch (error) {
    console.error('Error getting current date in timezone:', error);
    return new Date().toLocaleDateString('en-CA');
  }
};

// Get current time in specific timezone
export const getCurrentTimeInTimezone = (timezone = 'Asia/Kolkata') => {
  try {
    return new Date().toLocaleTimeString('en-GB', { 
      timeZone: timezone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    }); // en-GB gives HH:MM format
  } catch (error) {
    console.error('Error getting current time in timezone:', error);
    return new Date().toLocaleTimeString('en-GB', { hour12: false });
  }
};

// Check if appointment is today in the given timezone
export const isAppointmentToday = (appointmentDate, timezone = 'Asia/Kolkata') => {
  const today = getCurrentDateInTimezone(timezone);
  return appointmentDate === today;
};

// Check if appointment is in the past in the given timezone
export const isAppointmentPast = (appointmentDate, appointmentTime, timezone = 'Asia/Kolkata') => {
  try {
    const appointmentDateTime = new Date(`${appointmentDate}T${appointmentTime}:00`);
    const now = new Date();
    
    // Convert both to the same timezone for comparison
    const appointmentInTimezone = new Date(appointmentDateTime.toLocaleString('en-IN', { timeZone: timezone }));
    const nowInTimezone = new Date(now.toLocaleString('en-IN', { timeZone: timezone }));
    
    return appointmentInTimezone < nowInTimezone;
  } catch (error) {
    console.error('Error checking if appointment is past:', error);
    return false;
  }
};
